import React, { useId, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import Select from "react-select";
import { AdminServices } from "../../services/admin/AdminServices";
import { useLoginSelector } from "../../store/login/useLoginSelectors";

// Updated type definition: pincode is a required number, latitude and longitude are numbers
type StoreFormData = {
  storeName: string;
  addressLine1: string;
  addressLine2: string;
  locality: string;
  pincode: number | null; // pincode is a required number
  city: string;
  state: string;
  country: string;
  latitude: string; // Store latitude as number
  longitude: string; // Store longitude as number
  image?: string; // optional
  video?: string; // optional
};

interface MediaInput {
  file: File;
  mediaType?: string; // make optional since backend no longer requires it
  previewUrl: string;
  uploadedUrl?: string;
}

type StoreDetailsFormProps = {
  storeFormData: StoreFormData;
  setStoreFormData: React.Dispatch<React.SetStateAction<StoreFormData>>;
  counter: number;
  setCounter: React.Dispatch<React.SetStateAction<number>>;
};

const StoreDetailsForm: React.FC<StoreDetailsFormProps> = ({
  storeFormData,
  setStoreFormData,
  counter,
  setCounter,
}) => {
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setStoreFormData((prev) => ({
      ...prev,
      [name]:
        name === "latitude" || name === "longitude"
          ? value === ""
            ? 0
            : Number(value)
          : name === "pincode"
          ? Number(value)
          : value,
      ...(name === "state" && { city: "", locality: "" }), // Reset city and locality when state changes
      ...(name === "city" && { locality: "" }), // Reset locality when city changes
    }));
  };

  const handleSelectChange = (name: string, selectedOption: any) => {
    const value = selectedOption ? selectedOption.value : "";

    setStoreFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "state" && { city: "", locality: "" }), // Reset city and locality when state changes
      ...(name === "city" && { locality: "" }), // Reset locality when city changes
    }));
  };

  const handleBack = () => {
    setCounter(counter - 1);
  };

  const handleNext = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // if (mediaInputs.length === 0 || mediaInputs.some((m) => !m.uploadedUrl)) {
    //   toast.error("Please upload media before proceeding");
    //   return;
    // }
    const {
      storeName,
      addressLine1,
      locality,
      pincode,
      city,
      state,
      country,
      latitude,
      longitude,
    } = storeFormData;

    if (
      !storeName.trim() ||
      !addressLine1.trim() ||
      !locality.trim() ||
      pincode === undefined || // Ensure pincode is provided
      !city ||
      !state ||
      !country ||
      !latitude.trim() ||
      !longitude.trim()
    ) {
      toast.error("Please fill all the required fields");
      return;
    }

    setCounter(counter + 1);
  };

  const states = [
    { label: "Karnataka", value: "KA" },
    { label: "Haryana", value: "HR" },
    { label: "Uttar Pradesh", value: "UP" },
    { label: "Rajasthan", value: "RJ" },
    { label: "Delhi", value: "DLI" },
  ];

  const cities: {
    [stateCode: string]: { label: string; value: string }[];
  } = {
    HR: [
      { label: "Gurgaon", value: "GGN" },
      { label: "Sonipat", value: "SNP" },
      { label: "Panipat", value: "PNP" },
      { label: "Faridabad", value: "FDN" },
      { label: "Karnal", value: "KUN" },
      { label: "Jind", value: "JIND" },
      { label: "Bhiwani", value: "BNW" },
      { label: "Mahendragarh", value: "MHRG" },
      { label: "Rewari", value: "RE" },
      { label: "Jhajjar", value: "JHJ" },
      { label: "Palwal", value: "PWL" },
    ],
    KA: [{ label: "Bangalore", value: "BLR" }],
    UP: [
      { label: "Shamli", value: "SMQL" },
      { label: "Muzaffanagar", value: "MOZ" },
      { label: "Meerut", value: "MTC" },
      { label: "Hapur", value: "HPU" },
      { label: "Bulandshahr", value: "BSC" },
      { label: "Ghaziabad", value: "GZB" },
      { label: "Baghpat", value: "BPM" },
    ],
    RJ: [
      { label: "Alwar", value: "AWR" },
      { label: "Bharatpur", value: "BTE" },
    ],
    DLI: [{ label: "Delhi", value: "DLI" }],
  };

  const localities: {
    [cityCode: string]: { label: string; value: string }[];
  } = {
    GGN: [
      { label: "Sector 1", value: "SECTOR_1" },
      { label: "Sector 4", value: "SECTOR_4" },
      { label: "Sector 5", value: "SECTOR_5" },
      { label: "Sector 7", value: "SECTOR_7" },
      { label: "Sector 8", value: "SECTOR_8" },
      { label: "Sector 9", value: "SECTOR_9" },
      { label: "Sector 10", value: "SECTOR_10" },
      { label: "Sector 11", value: "SECTOR_11" },
      { label: "Sector 12", value: "SECTOR_12" },
      { label: "Sector 13", value: "SECTOR_13" },
      { label: "Sector 14", value: "SECTOR_14" },
      { label: "Sector 15", value: "SECTOR_15" },
      { label: "Sector 16", value: "SECTOR_16" },
      { label: "Sector 17", value: "SECTOR_17" },
      { label: "Sector 20", value: "SECTOR_20" },
      { label: "Sector 21", value: "SECTOR_21" },
      { label: "Sector 22", value: "SECTOR_22" },
      { label: "Sector 23", value: "SECTOR_23" },
      { label: "Sector 24", value: "SECTOR_24" },
      { label: "Sector 25", value: "SECTOR_25" },
      { label: "Sector 26", value: "SECTOR_26" },
      { label: "Sector 27", value: "SECTOR_27" },
      { label: "Sector 28", value: "SECTOR_28" },
      { label: "Sector 29", value: "SECTOR_29" },
      { label: "Sector 31", value: "SECTOR_31" },
      { label: "Sector 32", value: "SECTOR_32" },
      { label: "Sector 33", value: "SECTOR_33" },
      { label: "Sector 34", value: "SECTOR_34" },
      { label: "Sector 37", value: "SECTOR_37" },
      { label: "Sector 39", value: "SECTOR_39" },
      { label: "Sector 41", value: "SECTOR_41" },
      { label: "Sector 42", value: "SECTOR_42" },
      { label: "Sector 43", value: "SECTOR_43" },
      { label: "Sector 46", value: "SECTOR_46" },
      { label: "Sector 47", value: "SECTOR_47" },
      { label: "Sector 48", value: "SECTOR_48" },
      { label: "Sector 49", value: "SECTOR_49" },
      { label: "Sector 50", value: "SECTOR_50" },
      { label: "Sector 51", value: "SECTOR_51" },
      { label: "Sector 53", value: "SECTOR_53" },
      { label: "Sector 54", value: "SECTOR_54" },
      { label: "Sector 55", value: "SECTOR_55" },
      { label: "Sector 56", value: "SECTOR_56" },
      { label: "Sector 57", value: "SECTOR_57" },
      { label: "Sector 58", value: "SECTOR_58" },
      { label: "Sector 59", value: "SECTOR_59" },
      { label: "Sector 60", value: "SECTOR_60" },
      { label: "Sector 63", value: "SECTOR_63" },
      { label: "Sector 65", value: "SECTOR_65" },
      { label: "Sector 66", value: "SECTOR_66" },
      { label: "Sector 67", value: "SECTOR_67" },
      { label: "Sector 80", value: "SECTOR_80" },
      { label: "Sector 89", value: "SECTOR_89" },
      { label: "Sector 90", value: "SECTOR_90" },
      { label: "Sector 108", value: "SECTOR_108" },
      { label: "Sector 109", value: "SECTOR_109" },
      { label: "Sector 110", value: "SECTOR_110" },

      // Non-sector localities
      { label: "Dlf Phase 1", value: "DLF_PHASE1" },
      { label: "Dlf Phase 2", value: "DLF_PHASE_2" },
      { label: "Dlf Phase 3", value: "DLF_PHASE3" },
      { label: "DLF Phase 4", value: "DLF_PHASE_4" },
      { label: "Gwal Pahari", value: "GWAL_PAHARI" },
      { label: "Ambience Mall", value: "AMBIENCE_MALL" },
      { label: "Sahara Mall", value: "SAHARA_MALL" },
      { label: "Good Earth Mall", value: "GOOD_EARTHMALL" },
      { label: "Palam Vihar", value: "PALAM_VIHAR" },
      { label: "New Colony", value: "NEW_COLONY" },
    ],
    BLR: [{ label: "BTM Layout", value: "BTM_LAYOUT" }],
  };

  // Custom styles for React Select to match your existing design
  const customSelectStyles = {
    control: (provided: any) => ({
      ...provided,
      width: "100%",
      padding: "0.6rem",
      border: "1px solid #d4d4d8", // Tailwind's border-neutral-300
      borderRadius: "0.75rem",
    }),
    menu: (provided: any) => ({
      ...provided,
      borderRadius: "0.75rem", // Rounded corners on the dropdown container
      overflow: "hidden", // Ensures children don't overflow the border
      zIndex: 9999, // Keeps dropdown above other content
    }),
    menuList: (provided: any) => ({
      ...provided,
      borderRadius: "0.75rem", // Ensures inner options don't square off corners
      padding: 0, // Remove default padding if needed
    }),
    option: (provided: any, state: any) => ({
      ...provided,

      backgroundColor: state.isSelected
        ? "#8b5cf6"
        : state.isFocused
        ? "#f3f4f6"
        : "white", // Changed to green
      color: state.isSelected ? "white" : "#374151",
      "&:hover": {
        backgroundColor: state.isSelected ? "#7c3aed" : "#f3f4f6", // Darker green on hover when selected
      },
    }),
  };
  const { userDataById } = useLoginSelector();

  const [mediaInputs, setMediaInputs] = useState<MediaInput[]>([]);
  const [uploading, setUploading] = useState(false);

  const handleMediaChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const file = e.target.files[0]; // ONLY ONE FILE ALLOWED

    // Clear previous media preview
    setMediaInputs([]);

    const formatted: MediaInput = {
      file,
      mediaType: file.type.startsWith("image") ? "IMAGE" : "VIDEO",
      previewUrl: URL.createObjectURL(file), // temporary FE preview until uploaded
    };

    setMediaInputs([formatted]);
    setUploading(true);

    try {
      const { response } = await AdminServices.uploadMediaFile(
        file,
        userDataById?.data?.organization?.orgId || "",
        formatted.mediaType,
        file.name
      );


      const uploadedUrl = response.mediaUrl;
      const thumbnailUrl = response.thumbnailUrl; 

      // replace preview with actual thumbnail
      setMediaInputs([
        {
          ...formatted,
          uploadedUrl,
          previewUrl: thumbnailUrl || formatted.previewUrl,
        },
      ]);

      // 👉 STEP 1: Attach uploaded URL to storeFormData
      setStoreFormData((prev) => ({
        ...prev,
        image: formatted.mediaType === "IMAGE" ? uploadedUrl : undefined,
        video: formatted.mediaType === "VIDEO" ? uploadedUrl : undefined,
      }));

      toast.success("Media uploaded successfully");
    } catch (err) {
      console.error(err);
      toast.error("Failed to upload media");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />
      <form
        className="relative grid grid-cols-1 gap-6 pb-16 mx-auto store-info-form-container md:grid-cols-2"
        onSubmit={handleNext}
      >
        {/* Store Name + Media Input on same line */}
        <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Store Name */}
          <div className="flex flex-col">
            <label htmlFor="storeName" className="mb-1 text-lg">
              Store Name <span className="text-red-500">*</span>
            </label>
            <input
              id="storeName"
              name="storeName"
              value={storeFormData.storeName}
              onChange={handleChange}
              type="text"
              className="w-full p-4 border outline-none rounded-xl border-neutral-300 focus:border-[#8b5cf6]"
              placeholder="Enter store name"
            />
          </div>

          {/* Media Input */}
          <div className="flex flex-col">
            <label htmlFor="mediaInput" className="mb-1 text-lg">
              Media 
            </label>
            <input
              id="mediaInput"
              name="mediaInput"
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={(e) => handleMediaChange(e)}
              className="w-full p-4 border outline-none rounded-xl border-neutral-300 focus:border-[#8b5cf6]"
              placeholder="Enter media link"
            />
          </div>
        </div>

        {/* <div className="flex flex-col md:col-span-2">
          <label htmlFor="storeName" className="mb-1 text-lg">
            Store Name <span className="text-red-500">*</span>
          </label>
          <input
            id="storeName"
            name="storeName"
            value={storeFormData.storeName}
            onChange={handleChange}
            type="text"
            className="w-full p-4 border outline-none rounded-xl border-neutral-300 focus:border-[#8b5cf6]"
            placeholder="Enter store name"
          />
        </div> */}

        {/* Address Line 1 */}
        <div className="flex flex-col">
          <label htmlFor="addressLine1" className="mb-1 text-lg">
            Address Line 1 <span className="text-red-500">*</span>
          </label>
          <input
            id="addressLine1"
            name="addressLine1"
            value={storeFormData.addressLine1}
            onChange={handleChange}
            type="text"
            className="w-full p-4 border rounded-xl border-neutral-300 outline-none  focus:border-[#8b5cf6]"
            placeholder="Enter address line 1"
          />
        </div>

        {/* Address Line 2 */}
        <div className="flex flex-col">
          <label htmlFor="addressLine2" className="mb-1 text-lg">
            Address Line 2
          </label>
          <input
            id="addressLine2"
            name="addressLine2"
            value={storeFormData.addressLine2}
            onChange={handleChange}
            type="text"
            className="w-full p-4 border rounded-xl border-neutral-300 outline-none  focus:border-[#8b5cf6]"
            placeholder="Enter address line 2"
          />
        </div>

        {/* State */}
        <div className="flex flex-col">
          <label htmlFor="state" className="mb-1 text-lg">
            State <span className="text-red-500">*</span>
          </label>
          <Select
            id="state"
            name="state"
            value={
              states.find((state) => state.value === storeFormData.state) ||
              null
            }
            onChange={(selectedOption) =>
              handleSelectChange("state", selectedOption)
            }
            options={states}
            styles={customSelectStyles}
            placeholder="Select State"
            isClearable
            isSearchable
          />
        </div>

        {/* City */}
        <div className="flex flex-col">
          <label htmlFor="city" className="mb-1 text-lg">
            City <span className="text-red-500">*</span>
          </label>
          <Select
            id="city"
            name="city"
            value={
              (cities[storeFormData.state] || []).find(
                (city) => city.value === storeFormData.city
              ) || null
            }
            onChange={(selectedOption) =>
              handleSelectChange("city", selectedOption)
            }
            options={cities[storeFormData.state] || []}
            styles={customSelectStyles}
            placeholder="Select City"
            isClearable
            isSearchable
            isDisabled={!storeFormData.state}
          />
        </div>

        {/* Locality */}
        <div className="flex flex-col">
          <label htmlFor="locality" className="mb-1 text-lg">
            Locality <span className="text-red-500">*</span>
          </label>
          <Select
            id="locality"
            name="locality"
            value={
              (localities[storeFormData.city] || []).find(
                (locality) => locality.value === storeFormData.locality
              ) || null
            }
            onChange={(selectedOption) =>
              handleSelectChange("locality", selectedOption)
            }
            options={localities[storeFormData.city] || []}
            styles={customSelectStyles}
            placeholder="Select Locality"
            isClearable
            isSearchable
            isDisabled={!storeFormData.city}
            maxMenuHeight={200}
          />
        </div>

        {/* Pincode */}
        <div className="flex flex-col">
          <label htmlFor="pincode" className="mb-1 text-lg">
            Pincode <span className="text-red-500">*</span>
          </label>
          <input
            id="pincode"
            name="pincode"
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={storeFormData.pincode ?? ""}
            onChange={(e) => {
              const onlyDigits = e.target.value.replace(/\D/g, "");
              if (onlyDigits.length <= 6) {
                setStoreFormData((prev) => ({
                  ...prev,
                  pincode: onlyDigits === "" ? null : Number(onlyDigits),
                }));
              }
            }}
            className="p-4 border border-gray-300 rounded-xl outline-none  focus:border-[#8b5cf6]"
            placeholder="Enter 6-digit pincode"
          />
        </div>

        {/* Country */}
        <div className="flex flex-col md:col-span-2">
          <label htmlFor="country" className="mb-1 text-lg">
            Country <span className="text-red-500">*</span>
          </label>
          <input
            id="country"
            name="country"
            value={storeFormData.country}
            onChange={handleChange}
            type="text"
            className="w-full p-4 border rounded-xl border-neutral-300 outline-none  focus:border-[#8b5cf6]"
            placeholder="Enter country"
          />
        </div>

        {/* Latitude */}
        <div className="flex flex-col">
          <label htmlFor="latitude" className="mb-1 text-lg">
            Latitude <span className="text-red-500">*</span>
          </label>
          <input
            id="latitude"
            name="latitude"
            type="text"
            inputMode="decimal"
            value={storeFormData.latitude ?? ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const val = e.target.value;

              // Allow: empty, optional negative sign, digits, optional single dot
              const isValid = /^-?\d*\.?\d*$/.test(val);

              if (isValid) {
                setStoreFormData((prev) => ({
                  ...prev,
                  latitude: val,
                }));
              }
            }}
            className="w-full p-4 border rounded-xl border-neutral-300 outline-none  focus:border-[#8b5cf6]"
            placeholder="Enter latitude"
          />
        </div>

        {/* Longitude */}
        <div className="flex flex-col">
          <label htmlFor="longitude" className="mb-1 text-lg">
            Longitude <span className="text-red-500">*</span>
          </label>
          <input
            id="longitude"
            name="longitude"
            type="text"
            inputMode="decimal"
            value={storeFormData.longitude ?? ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const val = e.target.value;

              // Allow: empty, optional negative sign, digits, optional single dot
              const isValid = /^-?\d*\.?\d*$/.test(val);

              if (isValid) {
                setStoreFormData((prev) => ({
                  ...prev,
                  longitude: val,
                }));
              }
            }}
            className="w-full p-4 border rounded-xl border-neutral-300 outline-none  focus:border-[#8b5cf6]"
            placeholder="Enter longitude"
          />
        </div>

        {/* Buttons */}
        <div className="absolute bottom-0 right-0 flex gap-4 mt-4">
          <button
            type="button"
            onClick={handleBack}
            className="px-6 py-2 font-semibold transition bg-white border rounded text-violet-500 border-violet-500 hover:bg-violet-50"
          >
            Back
          </button>
          <button
            type="submit"
            className="px-6 py-2 font-semibold text-white transition rounded bg-violet-500 hover:bg-violet-600"
          >
            Next
          </button>
        </div>
      </form>
    </>
  );
};

export default StoreDetailsForm;
